/**
 * What-If Backtest — pure historical filter + replay.
 *
 * Takes the trader's full closed-trade history and replays it twice:
 *   1. ACTUAL — every trade as it happened (equity curve = real history)
 *   2. HYPOTHETICAL — the same trades, but skip ones that don't meet the
 *      configured filter rule(s)
 *
 * The point isn't to argue the trader should/shouldn't have taken any
 * specific trade — it's to show whether the analyzer-recommended rules
 * would actually have paid. If "only-trade-with-VWAP-confluence" produces
 * a +$1,200 / week vs the actual +$300 / week, that's evidence the
 * compliance gap matters. If the hypothetical equity curve is flat or
 * worse, the rule is wrong (or the analyzer's recommendation needs more
 * data).
 *
 * Pure: takes inputs in, returns a backtest result. No I/O.
 *
 * Supported rule sets (extensible — add more by extending applyRule):
 *   'recommended-confluences' — only trades where ALL of
 *     setupDetails[trade.setup].recommendedConfluences are present in
 *     trade.confluences
 *   'no-counter-confluences'  — only trades where trade.counterConfluences
 *     is empty (no documented warning was overridden)
 *   'all'                     — apply BOTH of the above
 *
 * Returns null when there's no usable trade history (no trades, or all
 * trades lack `date` / `setup` / netPnl).
 */

const RULES = Object.freeze({
  RECOMMENDED_CONFLUENCES: 'recommended-confluences',
  NO_COUNTER_CONFLUENCES:  'no-counter-confluences',
  ALL:                     'all',
});

/**
 * Decide whether a trade passes the recommended-confluences rule for its
 * setup. Trades with no setup OR a setup that has no recommendedConfluences
 * configured PASS by default — there's no rule to fail.
 */
const passesRecommendedConfluences = (trade, setupDetails) => {
  if (!trade?.setup) return true;
  const recommended = setupDetails?.[trade.setup]?.recommendedConfluences || [];
  if (recommended.length === 0) return true;
  const have = new Set(Array.isArray(trade.confluences) ? trade.confluences : []);
  for (const r of recommended) if (!have.has(r)) return false;
  return true;
};

/**
 * Decide whether a trade passes the no-counter-confluences rule.
 * Trades with an empty counterConfluences array PASS.
 */
const passesNoCounter = (trade) => {
  const counters = Array.isArray(trade?.counterConfluences) ? trade.counterConfluences : [];
  return counters.length === 0;
};

/**
 * Apply the configured rule set. Returns true when the trade SURVIVES the
 * filter (i.e., would have been taken in the hypothetical).
 */
export const tradeSurvivesRule = (trade, ruleSet, setupDetails) => {
  switch (ruleSet) {
    case RULES.RECOMMENDED_CONFLUENCES:
      return passesRecommendedConfluences(trade, setupDetails);
    case RULES.NO_COUNTER_CONFLUENCES:
      return passesNoCounter(trade);
    case RULES.ALL:
      return passesRecommendedConfluences(trade, setupDetails) && passesNoCounter(trade);
    default:
      return true;
  }
};

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

/**
 * Build cumulative equity curve from a sorted-by-date trade list. Returns
 * an array of `{ date, cumulative, pnl }` ready for line-chart rendering.
 * Multiple trades on the same date are aggregated into a single point at
 * end-of-day cumulative.
 */
const buildEquityCurve = (sortedTrades) => {
  const out = [];
  let cumulative = 0;
  let lastDate = null;
  for (const t of sortedTrades) {
    cumulative += t.netPnl;
    if (lastDate === t.date && out.length > 0) {
      out[out.length - 1].cumulative = cumulative;
      out[out.length - 1].pnl += t.netPnl;
    } else {
      out.push({ date: t.date, cumulative, pnl: t.netPnl });
      lastDate = t.date;
    }
  }
  return out;
};

const summarize = (trades) => {
  const tradeCount = trades.length;
  const wins = trades.filter(isWin).length;
  const losses = tradeCount - wins;
  const netPnl = trades.reduce((s, t) => s + t.netPnl, 0);
  const rs = trades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
  const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;
  return {
    tradeCount,
    wins,
    losses,
    winRate: tradeCount > 0 ? wins / tradeCount : 0,
    netPnl,
    avgR,
    equityCurve: buildEquityCurve(trades),
  };
};

/**
 * Per-setup attribution — how much each setup contributed to actual vs
 * hypothetical, and how many trades each setup had filtered out.
 */
const perSetupAttribution = (actual, hypothetical) => {
  const map = new Map();  // setup → { setup, actualNet, hypotheticalNet, actualCount, hypotheticalCount, skipped }
  const ensure = (setup) => {
    if (!map.has(setup)) {
      map.set(setup, {
        setup, actualNet: 0, hypotheticalNet: 0,
        actualCount: 0, hypotheticalCount: 0, skipped: 0,
      });
    }
    return map.get(setup);
  };
  for (const t of actual) {
    const s = ensure(t.setup || '(no setup)');
    s.actualNet += t.netPnl;
    s.actualCount++;
  }
  for (const t of hypothetical) {
    const s = ensure(t.setup || '(no setup)');
    s.hypotheticalNet += t.netPnl;
    s.hypotheticalCount++;
  }
  for (const s of map.values()) {
    s.skipped = s.actualCount - s.hypotheticalCount;
  }
  return [...map.values()].sort((a, b) => (b.hypotheticalNet - b.actualNet) - (a.hypotheticalNet - a.actualNet));
};

/**
 * Public API.
 *
 * @param {Object} args
 * @param {Array}  args.trades           closed trades (must have date, setup, netPnl-or-pnl)
 * @param {Object} args.setupDetails     { [setupName]: { recommendedConfluences: [] } }
 * @param {Function} [args.getTradeNetPnL]  for commission-aware netPnl
 * @param {Function} [args.getAccountById]
 * @param {string} [args.ruleSet='recommended-confluences']
 * @returns {WhatIfBacktest|null}
 */
export const runBacktest = ({
  trades = [],
  setupDetails = {},
  getTradeNetPnL,
  getAccountById,
  ruleSet = RULES.RECOMMENDED_CONFLUENCES,
} = {}) => {
  if (!Array.isArray(trades) || trades.length === 0) return null;

  // Enrich with commission-aware netPnl AND require a date for windowing.
  const netPnlFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
  };
  const enriched = trades
    .filter(t => t && t.date)
    .map(t => ({ ...t, netPnl: netPnlFor(t) }))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  if (enriched.length === 0) return null;

  const actual = enriched;
  const hypothetical = enriched.filter(t => tradeSurvivesRule(t, ruleSet, setupDetails));
  const skipped = enriched.filter(t => !tradeSurvivesRule(t, ruleSet, setupDetails));

  const actualSummary = summarize(actual);
  const hypoSummary = summarize(hypothetical);
  const skippedSummary = summarize(skipped);

  const perSetup = perSetupAttribution(actual, hypothetical);

  return {
    generatedAt: new Date().toISOString(),
    ruleSet,
    actual: actualSummary,
    hypothetical: {
      ...hypoSummary,
      skippedCount: skippedSummary.tradeCount,
      skippedNetPnl: skippedSummary.netPnl,
    },
    delta: {
      netPnl: hypoSummary.netPnl - actualSummary.netPnl,
      winRate: hypoSummary.winRate - actualSummary.winRate,
      tradeCount: hypoSummary.tradeCount - actualSummary.tradeCount,
    },
    perSetup,
  };
};

// Exports for tests
export {
  RULES,
  passesRecommendedConfluences,
  passesNoCounter,
  buildEquityCurve,
  summarize,
  perSetupAttribution,
};
